import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteMode } from '../context/SiteModeContext';
import { ArrowRight } from 'lucide-react';
import Logo from './Logo';

const HeroSection = () => {
  const { t } = useLanguage();
  const { features, isPortfolio } = useSiteMode();
  const heroRef = useRef(null);

  // First section below the hero, so the primary CTA and the scroll indicator
  // stay meaningful when sections are disabled by the active site mode.
  const nextSectionHref = features.services
    ? '#what-we-do'
    : features.experience
      ? '#experience'
      : '#portfolio';
  const primaryCtaHref = features.experience ? '#experience' : '#portfolio';
  const primaryCtaLabel = features.experience ? t.hero.viewExperience : t.nav.portfolio;
  // Portfolio mode drops the agency pitch for a line about the work itself.
  const subheading = (isPortfolio && t.hero.portfolioSubheading) || t.hero.subheading;

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      
      heroRef.current.style.setProperty('--mouse-x', `${x}px`);
      heroRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      ref={heroRef}
      className="min-h-screen flex flex-col justify-center relative overflow-hidden bg-black"
    >
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        {/* Primary gradient orb */}
        <div 
          className="absolute w-[800px] h-[800px] rounded-full opacity-20 blur-[120px] animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(6,182,212,0.4) 0%, transparent 70%)',
            top: '10%',
            left: '60%',
            transform: 'translate(var(--mouse-x, 0), var(--mouse-y, 0))'
          }}
        />
        {/* Secondary gradient orb */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full opacity-10 blur-[100px] animate-float-delayed"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
            bottom: '20%',
            left: '10%'
          }}
        />
        
        {/* Grid pattern with perspective */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            perspective: '1000px',
            transform: 'rotateX(60deg) scale(2.5)',
            transformOrigin: 'center top'
          }}
        />
        
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.015] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="max-w-5xl">
          {/* Original Logo Image */}
          <div className="mb-12 animate-fade-in-up">
            <Logo size="hero" className="lg:scale-110 origin-left" />
          </div>

          {/* Subheading */}
          <p className="text-xl sm:text-2xl lg:text-3xl text-zinc-400 font-extralight leading-relaxed max-w-3xl mb-16 animate-fade-in-up animation-delay-100">
            {subheading}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
            <button
              onClick={() => scrollToSection(primaryCtaHref)}
              className="group relative inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-black font-medium tracking-wide overflow-hidden transition-all duration-500 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]"
            >
              <span className="relative z-10">{primaryCtaLabel}</span>
              <ArrowRight className="relative z-10 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </button>
            
            {features.discovery && (
              <button
                onClick={() => scrollToSection('#contact')}
                className="group relative inline-flex items-center justify-center gap-3 px-10 py-5 border border-zinc-700 text-zinc-300 font-medium tracking-wide backdrop-blur-sm bg-white/5 hover:bg-white/10 hover:border-zinc-500 transition-all duration-500"
              >
                {t.hero.contact}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
        <button 
          onClick={() => scrollToSection(nextSectionHref)}
          className="group flex flex-col items-center gap-2 text-zinc-600 hover:text-white transition-colors duration-300"
        >
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-zinc-600 to-transparent relative overflow-hidden">
            <div className="absolute w-full h-4 bg-white animate-scroll-line" />
          </div>
        </button>
      </div>

      {/* Decorative lines */}
      <div className="absolute top-0 left-1/4 w-px h-32 bg-gradient-to-b from-transparent via-zinc-800 to-transparent" />
      <div className="absolute top-0 right-1/3 w-px h-48 bg-gradient-to-b from-transparent via-zinc-800 to-transparent" />
    </section>
  );
};

export default HeroSection;
